import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";

// ====== styles ======
const TableWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const Input = styled.input`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
  width: 320px;
`;

const ScrollContainer = styled.div`
  max-height: 720px;
  overflow-y: auto;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 40%;
  }
  th:nth-child(2),
  td:nth-child(2) {
    width: 18%;
  }
  th:nth-child(3),
  td:nth-child(3) {
    width: 22%;
  }
  th:nth-child(4),
  td:nth-child(4) {
    width: 20%;
  }

  th {
    background: #9848c7; /* zakupy kolor */
    color: #fff;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  tr:hover {
    background-color: #eef6ff;
  }
`;

// ====== helpers ======
const ymKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

const prevMonthKey = (key, n = 1) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - n);
  return ymKey(d.getFullYear(), d.getMonth() + 1);
};

// Purchase value = K_42 + K_43 (only positives count)
const parsePurchaseVal = (row) => {
  const net = parseFloat(row.K_42) || 0;
  const vat = parseFloat(row.K_43) || 0;
  return net + vat;
};

const safeDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d) ? null : d;
};

const formatPLN = (v) =>
  Number(v || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ====== component (ZAKUPY) ======
const ChurnedSuppliersTable = ({ zakupyData = [] }) => {
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");

  // latest month in dataset (by Rok/Miesiac)
  const latestKey = useMemo(() => {
    if (!zakupyData.length) return null;
    return zakupyData.reduce((latest, r) => {
      const y = parseInt(r.Rok, 10);
      const m = parseInt(r.Miesiac, 10);
      if (isNaN(y) || isNaN(m)) return latest;
      const k = ymKey(y, m);
      return !latest || latest < k ? k : latest;
    }, null);
  }, [zakupyData]);

  useEffect(() => {
    if (!zakupyData.length || !latestKey) {
      setRows([]);
      return;
    }

    // Months: m3, m2, m1 must have positive purchases; m0 must have none.
    const m0 = latestKey; // current month (no purchase)
    const m1 = prevMonthKey(m0, 1);
    const m2 = prevMonthKey(m0, 2);
    const m3 = prevMonthKey(m0, 3);
    const last3Set = new Set([m1, m2, m3]);

    // Aggregate positive sums per supplier per month
    // key: "name||nr" -> { [ym]: { posSum: number } }
    const perSuppMonth = {};
    const lastPosDateInLast3 = {};

    for (const row of zakupyData) {
      const name = row.NazwaDostawcy || "Nieznany dostawca";
      const rawNr = (row.NrDostawcy ?? "").toString().trim();
      const nrDostawcy = rawNr || "—";
      const y = parseInt(row.Rok, 10);
      const m = parseInt(row.Miesiac, 10);
      if (isNaN(y) || isNaN(m)) continue;

      const k = ymKey(y, m);
      const key = `${name}||${nrDostawcy}`;
      const val = parsePurchaseVal(row);

      if (!perSuppMonth[key]) perSuppMonth[key] = {};
      if (!perSuppMonth[key][k]) perSuppMonth[key][k] = { posSum: 0 };

      // Only positive values count toward "purchased this month"
      if (val > 0) {
        perSuppMonth[key][k].posSum += val;

        if (last3Set.has(k)) {
          const d =
            safeDate(row.DataZakupu) || safeDate(row.DataWystawienia) || null;
          if (d) {
            const curr = lastPosDateInLast3[key];
            if (!curr || d > curr) lastPosDateInLast3[key] = d;
          }
        }
      }
    }

    // Build result: need posSum > 0 in m3, m2, m1 AND none in m0
    const result = [];
    for (const key of Object.keys(perSuppMonth)) {
      const [name, nrDostawcy] = key.split("||");
      const p1 = perSuppMonth[key][m1]?.posSum || 0;
      const p2 = perSuppMonth[key][m2]?.posSum || 0;
      const p3 = perSuppMonth[key][m3]?.posSum || 0;
      const p0 = perSuppMonth[key][m0]?.posSum || 0;

      if (p1 > 0 && p2 > 0 && p3 > 0 && p0 === 0) {
        const sumLast3 = p1 + p2 + p3;
        const lastDate = lastPosDateInLast3[key]
          ? lastPosDateInLast3[key].toLocaleDateString("pl-PL")
          : "—";

        result.push({
          supplier: name,
          nrDostawcy,
          valueLast3: sumLast3,
          lastDateBought: lastDate,
        });
      }
    }

    // Sort by valueLast3 DESC, then supplier ASC
    result.sort(
      (a, b) =>
        b.valueLast3 - a.valueLast3 ||
        a.supplier.localeCompare(b.supplier)
    );
    setRows(result);
  }, [zakupyData, latestKey]);

  const filtered = rows.filter((r) => {
    const q = filterText.trim().toLowerCase();
    if (!q) return true;
    return (
      r.supplier.toLowerCase().includes(q) ||
      (r.nrDostawcy || "").toLowerCase().includes(q)
    );
  });

  const exportCSV = () => {
    const headers = [
      "Dostawca",
      "Nr dostawcy",
      "Wartość zakupów z ostatnich 3 mies. (PLN)",
      "Data ostatniego zakupu",
    ];

    const body = filtered.map((r) => [
      r.supplier,
      r.nrDostawcy || "",
      formatPLN(r.valueLast3),
      r.lastDateBought,
    ]);

    const sep = ";";
    const esc = (s) => {
      const v = String(s ?? "");
      return /[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };

    const csv = [headers, ...body]
      .map((row) => row.map(esc).join(sep))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `stali_dostawcy_ktorzy_odeszli.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <TableWrapper>
      <TitleWrapper>
        Stali dostawcy, od których przestaliśmy kupować
        <div
          data-tooltip-id="churned-suppliers-tooltip"
          style={{ cursor: "pointer" }}
        >
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="churned-suppliers-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Stali dostawcy, od których przestaliśmy kupować</strong>
            <br />
            Dostawca pojawia się w zakupach przez <b>3 miesiące z rzędu</b>, a
            w bieżącym (4.) miesiącu <b>nie ma żadnego zakupu</b>.
            <br />
            (Korekty / ujemne kwoty nie liczą się jako zakup – bierzemy tylko
            dodatnie wartości K_42 + K_43).
          </>
        }
      />

      <ControlsRow>
        <div>
          <label>Dostawca / Nr dostawcy: </label>
          <Input
            type="text"
            placeholder="Wpisz nazwę lub nr dostawcy…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <ExportButton onClick={exportCSV}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </ControlsRow>

      <ScrollContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Dostawca</th>
              <th>Nr dostawcy</th>
              <th>Wartość zakupów z ostatnich 3 mies. (PLN)</th>
              <th>Data ostatniego zakupu</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((r, i) => (
                <tr key={`${r.supplier}-${r.nrDostawcy}-${i}`}>
                  <td>{r.supplier}</td>
                  <td>{r.nrDostawcy}</td>
                  <td>{formatPLN(r.valueLast3)}</td>
                  <td>{r.lastDateBought}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">Brak dostawców spełniających kryteria.</td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </ScrollContainer>
    </TableWrapper>
  );
};

export default ChurnedSuppliersTable;
